import React, { useCallback, useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { useFetchPostV2 } from "hooks/fetch/useFetchPostV2";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { UserProfileContext } from "contexts/UserProfileContext";
import { styled } from "@mui/material/styles";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { SettingsSection } from "components/Settings/SettingsSection";
import { NewRoleDialog } from "components/Settings/widgets/NewRoleDialog";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useLoadCounter } from "hooks/useLoadCounter";

export function RoleManagementSection() {
  const history = useHistory();
  const [createDialog, setCreateDialog] = useState(false);

  const {
    loading: rolesLoading,
    error: rolesError,
    response: roles = [],
    refetch: refetchRoles
  } = useFetchGetV2("settings/roles");

  const tableLoadCount = useLoadCounter(rolesLoading);

  const {
    loading: entitlementsLoading,
    response: entitlements = []
  } = useFetchGetV2("settings/entitlements");

  const { loading: postRoleLoading, postCallback: postRole } = useFetchPostV2(
    "settings/roles"
  );

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { currentUserId } = useContext(UserProfileContext);

  const onSubmitPost = useCallback(
    async value => {
      const payload = {
        name: value.name,
        entitlements: [...value.entitlements],
        owners: [currentUserId],
        users: [currentUserId]
      };

      try {
        const response = await postRole(payload);
        setCreateDialog(false);
        addSnackbarToQueue({
          message: "A new role successfully saved.",
          severity: "success"
        });

        if (response?.id) {
          history.push(`/pages/settings/role/${response.id}`);
        } else {
          await refetchRoles();
        }
      } catch (error) {
        console.error(error);
        addSnackbarToQueue({
          message: "Create role failed to save.",
          severity: "error"
        });
      }
    },
    [addSnackbarToQueue, currentUserId, history, postRole, refetchRoles]
  );

  return (
    <>
      <SettingsSection title="Roles">
        <RolesTableContainer component={Paper}>
          <ProgressIndicatorContainer loading={rolesLoading} error={rolesError}>
            <PerfectScrollbar>
              <RolesTable stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell data-col={1}>ID</TableCell>
                    <TableCell data-col={2}>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  data-qa_loading={rolesLoading}
                  data-qa_load_count={tableLoadCount}
                >
                  {roles.map(role =>
                    role ? (
                      <TableRow key={role.id}>
                        <TableCell data-col={1}>{role.id}</TableCell>
                        <TableCell data-col={2}>
                          <Link to={`/pages/settings/role/${role.id}`}>
                            {role.name}
                          </Link>
                        </TableCell>
                      </TableRow>
                    ) : null
                  )}
                </TableBody>
              </RolesTable>
            </PerfectScrollbar>
          </ProgressIndicatorContainer>
        </RolesTableContainer>
        <ButtonWrapper>
          <Button
            onClick={() => setCreateDialog(true)}
            color="primary"
            variant="contained"
            disabled={postRoleLoading || rolesLoading || rolesError}
            data-qa="Settings-Button-Add-New-Role"
          >
            Add
          </Button>
        </ButtonWrapper>
      </SettingsSection>
      <NewRoleDialog
        open={createDialog}
        loading={postRoleLoading || rolesLoading || entitlementsLoading}
        entitlements={entitlements}
        onClose={() => setCreateDialog(false)}
        onSubmit={onSubmitPost}
      />
    </>
  );
}

const RolesTableContainer = styled(TableContainer)`
  height: 325px;
`;

const RolesTable = styled(Table)`
  height: max-content;
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing(1)};
`;
